// Notifications (Phase 46): the typed event registry. The one place notification types, their
// audience, group, digest behaviour, default channels, WhatsApp eligibility, group keys and deep
// links are defined (D-03). A new event is one registry entry here plus its copy in
// notificationCopy.ts.
//
// This module is PURE and DEPENDENCY-FREE (no imports), same as moderation.ts / gamification.ts:
//  - the BACKEND imports it through the compiled `shared` package entry (models, notification
//    service, delivery worker, email/push/WhatsApp channels);
//  - the FRONTEND imports the TS SOURCE through the `@shared/*` alias (bell, inbox, preferences
//    matrix, admin delivery log).
//
// Spec: .planning/phases/46-notifications/46-CONTEXT.md (D-03, D-08, D-09, D-15, D-19, D-22).

// ─── Types ─────────────────────────────────────────────────────────────────────

export const notificationTypeMap = {
  claim_approved: "claim_approved",
  claim_rejected: "claim_rejected",
  store_review_received: "store_review_received",
  review_merchant_replied: "review_merchant_replied",
  review_commented: "review_commented",
  review_liked: "review_liked",
  tier_reached: "tier_reached",
  points_awarded: "points_awarded",
  escalation_opened: "escalation_opened",
  // Phase 49 onboarding (first 7 days).
  welcome: "welcome",
  onboarding_day3: "onboarding_day3",
  onboarding_day7: "onboarding_day7",
} as const;
export type NotificationType = keyof typeof notificationTypeMap;

// ─── Channels ──────────────────────────────────────────────────────────────────

// Named NotificationDeliveryChannel, never NotificationChannel: backend/src/models/AppSettings.ts
// already exports a `NotificationChannel` type meaning the Slack digest channels, and the two
// would clash wherever both are imported.
export const notificationDeliveryChannelMap = {
  inApp: "inApp",
  email: "email",
  whatsapp: "whatsapp",
  push: "push",
} as const;
export type NotificationDeliveryChannel = keyof typeof notificationDeliveryChannelMap;

// Channels that leave the site and therefore go through the delivery queue.
export const EXTERNAL_CHANNELS = ["email", "whatsapp", "push"] as const;
export type ExternalChannel = (typeof EXTERNAL_CHANNELS)[number];

// ─── Audiences and groups ──────────────────────────────────────────────────────

// D-22: only public users and admins receive notifications in v1. Moderator notifications are
// deferred, so there is deliberately no moderator audience.
export const notificationAudienceMap = {
  public: "public",
  admin: "admin",
} as const;
export type NotificationAudience = keyof typeof notificationAudienceMap;

export const notificationGroupMap = {
  account: "account",
  merchant: "merchant",
  reviews: "reviews",
  rewards: "rewards",
  admin: "admin",
} as const;
export type NotificationGroup = keyof typeof notificationGroupMap;

// ─── Registry ──────────────────────────────────────────────────────────────────

export type NotificationRegistryEntry = {
  audience: NotificationAudience;
  group: NotificationGroup;
  // Digestable types never send instantly on an external channel; they roll into the daily digest.
  digestable: boolean;
  defaults: Record<NotificationDeliveryChannel, boolean>;
  whatsappEligible: boolean;
};

// D-15 defaults: in-app on for every type; email, push and WhatsApp on only for the four
// high-signal types. O-3: for DIGESTABLE types the email cell means "include in the 20:00 daily
// digest email" and the push cell means "include in the one push summary"; WhatsApp is never used
// for them (a roll-up of likes and points reads as MARKETING to Meta).
export const NOTIFICATION_REGISTRY: Record<NotificationType, NotificationRegistryEntry> = {
  claim_approved: {
    audience: "public",
    group: "merchant",
    digestable: false,
    defaults: { inApp: true, email: true, whatsapp: true, push: true },
    whatsappEligible: true,
  },
  claim_rejected: {
    audience: "public",
    group: "merchant",
    digestable: false,
    defaults: { inApp: true, email: true, whatsapp: true, push: true },
    whatsappEligible: true,
  },
  store_review_received: {
    audience: "public",
    group: "merchant",
    digestable: false,
    defaults: { inApp: true, email: true, whatsapp: true, push: true },
    whatsappEligible: true,
  },
  review_merchant_replied: {
    audience: "public",
    group: "reviews",
    digestable: false,
    defaults: { inApp: true, email: true, whatsapp: true, push: true },
    whatsappEligible: true,
  },
  review_commented: {
    audience: "public",
    group: "reviews",
    digestable: true,
    defaults: { inApp: true, email: true, whatsapp: false, push: false },
    whatsappEligible: false,
  },
  review_liked: {
    audience: "public",
    group: "reviews",
    digestable: true,
    defaults: { inApp: true, email: true, whatsapp: false, push: false },
    whatsappEligible: false,
  },
  tier_reached: {
    audience: "public",
    group: "rewards",
    digestable: false,
    defaults: { inApp: true, email: false, whatsapp: false, push: false },
    whatsappEligible: false,
  },
  points_awarded: {
    audience: "public",
    group: "rewards",
    digestable: true,
    defaults: { inApp: true, email: true, whatsapp: false, push: false },
    whatsappEligible: false,
  },
  escalation_opened: {
    audience: "admin",
    group: "admin",
    digestable: false,
    defaults: { inApp: true, email: false, whatsapp: false, push: false },
    whatsappEligible: false,
  },
  // Phase 49 (D-17): onboarding messages are on for every channel by default and never digested.
  // D-24: only welcome and day 3 have a WhatsApp template; day 7 is email/push/in-app only.
  welcome: {
    audience: "public",
    group: "account",
    digestable: false,
    defaults: { inApp: true, email: true, whatsapp: true, push: true },
    whatsappEligible: true,
  },
  onboarding_day3: {
    audience: "public",
    group: "account",
    digestable: false,
    defaults: { inApp: true, email: true, whatsapp: true, push: true },
    whatsappEligible: true,
  },
  onboarding_day7: {
    audience: "public",
    group: "account",
    digestable: false,
    defaults: { inApp: true, email: true, whatsapp: true, push: true },
    whatsappEligible: false,
  },
};

// ─── Merchant review sources ───────────────────────────────────────────────────

// D-19: merchants are notified on every published review that lands on their page, first-party
// plus these external sources only (never filter by `isExternal`). Open item O-1: shopify_widget
// and whatsapp_flow are single real-time writes in code but are excluded per the D-19 literal;
// adding them is one line here.
export const STORE_REVIEW_NOTIFY_SOURCES = ["facebook_group"] as const;
export type StoreReviewNotifySource = (typeof STORE_REVIEW_NOTIFY_SOURCES)[number];

// Ratings at or below this use the "needs your reply" copy and WhatsApp template.
export const LOW_RATING_THRESHOLD = 2;

// ─── Delivery vocabulary (shared with the admin delivery log) ──────────────────

export const deliveryStatusMap = {
  queued: "queued",
  sending: "sending",
  sent: "sent",
  failed: "failed",
  skipped: "skipped",
} as const;
export type DeliveryStatus = keyof typeof deliveryStatusMap;

export const deliveryKindMap = {
  instant: "instant",
  digest: "digest",
} as const;
export type DeliveryKind = keyof typeof deliveryKindMap;

// Terminal skips: the row will never be sent.
export const deliverySkipReasonMap = {
  pref_off: "pref_off",
  not_eligible: "not_eligible",
  no_consent: "no_consent",
  wa_daily_cap: "wa_daily_cap",
  template_not_configured: "template_not_configured",
  // Phase 49: the template exists but Meta classified it as something other than UTILITY.
  template_not_utility: "template_not_utility",
  channel_disabled: "channel_disabled",
  no_subscription: "no_subscription",
  permanent_error: "permanent_error",
} as const;
export type DeliverySkipReason = keyof typeof deliverySkipReasonMap;

// Holds: the row stays queued and is retried later. A missing provider key is a HOLD, not a
// terminal skip, so rows queued before an env var is set are delivered once it exists.
export const deliveryHoldReasonMap = {
  quiet_hours: "quiet_hours",
  backoff: "backoff",
  provider_not_configured: "provider_not_configured",
} as const;
export type DeliveryHoldReason = keyof typeof deliveryHoldReasonMap;

// The result every backend channel (email, WhatsApp, push) returns to the delivery worker. Lives
// here so the channel plans can be built in parallel against one contract.
export type ChannelOutcome =
  | { status: "sent"; providerId?: string }
  | { status: "retry"; error: string }
  | { status: "failed"; error: string }
  | { status: "skipped"; reason: DeliverySkipReason; error?: string }
  | { status: "held"; reason: "provider_not_configured" };

// ─── Constants ─────────────────────────────────────────────────────────────────

export const DEFAULT_DIGEST_HOUR = 20;
export const DEFAULT_QUIET_START_HOUR = 23;
export const DEFAULT_QUIET_END_HOUR = 9;
export const DEFAULT_WA_DAILY_CAP = 3;
export const DEFAULT_REVIEW_RECENCY_DAYS = 14;
export const NOTIFICATION_RETENTION_DAYS = 90;
export const NOTIFICATION_ACTORS_KEPT = 5;
export const PUSH_SUBSCRIPTIONS_PER_USER_MAX = 10;
export const PUSH_PROMPT_SNOOZE_DAYS = 30;

// ─── Payload ───────────────────────────────────────────────────────────────────

// Everything a notification row may carry for rendering and linking. All optional; each type uses
// its own subset. Values are plain text (never HTML).
export type NotificationPayload = {
  storeId?: string;
  storeName?: string;
  storeSlug?: string;
  reviewId?: string;
  commentId?: string;
  rating?: number;
  reason?: string;
  points?: number;
  level?: number;
  tierKey?: string;
  userId?: string;
  itemType?: string;
  moderatorName?: string;
  escalationId?: string;
  auto?: boolean;
  // Display name of the (latest) actor, used where only one name is shown (WhatsApp params).
  actorName?: string;
  // ─── Phase 49 onboarding ───
  firstName?: string;
  tierAr?: string;
  // Fact ladder rung (UI-SPEC): 1 likes/comments, 2 merchant reply, 3 one review from next tier,
  // 4 nudge to review a store.
  ladderRung?: 1 | 2 | 3 | 4;
  reactions?: number; // likes
  commentCount?: number;
  merchantReplied?: boolean;
  hasReviewed?: boolean;
  pickReason?: "signup_store" | "last_viewed" | "same_category" | "popular";
  // The store of the user's first review (rung-4 reviewer context line).
  contextStoreName?: string;
  categoryName?: string;
  waVariant?: "a" | "b";
  // The single variable sentence of onboarding_in_week, built by the backend from ONBOARDING_COPY.
  waLine?: string;
  waStoreSlug?: string;
};

// ─── WhatsApp templates ────────────────────────────────────────────────────────

// One Meta template per key; bodies live in notificationCopy.ts (WHATSAPP_TEMPLATE_COPY).
export const WHATSAPP_TEMPLATE_KEYS = [
  "claim_approved",
  "claim_rejected",
  "store_review_received",
  "store_review_received_low",
  "review_merchant_replied",
  // Phase 49 (D-24): exactly two onboarding templates.
  "account_created",
  "onboarding_in_week",
] as const;
export type WhatsappTemplateKey = (typeof WHATSAPP_TEMPLATE_KEYS)[number];

export const resolveWhatsappTemplateKey = ({
  type,
  rating,
}: {
  type: NotificationType;
  rating?: number;
}): WhatsappTemplateKey | null => {
  if (!NOTIFICATION_REGISTRY[type].whatsappEligible) return null;
  // Onboarding types map to differently named templates; onboarding_day7 has none (D-24).
  if (type === "welcome") return "account_created";
  if (type === "onboarding_day3") return "onboarding_in_week";
  if (type === "onboarding_day7") return null;
  // Every rating uses store_review_received. Meta reclassified store_review_received_low as
  // MARKETING (2026-10), so the low variant is never sent; the key stays only so stored settings
  // and the admin form keep their shape. `rating` is still carried as {{3}} in the body.
  void rating;
  return (WHATSAPP_TEMPLATE_KEYS as readonly string[]).includes(type) ? (type as WhatsappTemplateKey) : null;
};

const TEMPLATE_PARAM_MAX = 60;

// Meta error 132018: body params may not contain newlines, tabs or 4+ consecutive spaces
// (RESEARCH A3). Store and actor names are user-controlled, so every param is sanitised.
export const sanitizeTemplateParam = (s: string): string => {
  const clean = String(s ?? "")
    .replace(/[\r\n\t\u000b\u000c]+/g, " ")
    .replace(/ {2,}/g, " ")
    .trim();
  // Array.from so a cap never splits a surrogate pair.
  return Array.from(clean).slice(0, TEMPLATE_PARAM_MAX).join("").trim();
};

// Meta rejects empty params, so each slot has a neutral fallback.
const FALLBACK_ACTOR = "حد";
const FALLBACK_STORE = "متجرك";

const paramOr = (value: string | undefined, fallback: string): string => {
  const clean = sanitizeTemplateParam(value ?? "");
  return clean || fallback;
};

// Params in exactly the {{1}}..{{n}} order the template bodies in notificationCopy.ts use.
export const buildWhatsappBodyParams = ({
  templateKey,
  payload,
}: {
  templateKey: WhatsappTemplateKey;
  payload: NotificationPayload;
}): string[] => {
  const store = paramOr(payload.storeName, FALLBACK_STORE);
  switch (templateKey) {
    case "claim_approved":
    case "claim_rejected":
    case "review_merchant_replied":
      return [store];
    case "store_review_received":
    case "store_review_received_low":
      return [
        paramOr(payload.actorName, FALLBACK_ACTOR),
        store,
        paramOr(typeof payload.rating === "number" ? String(payload.rating) : undefined, "0"),
      ];
    // No fallback name (UI-SPEC S7): an empty param makes the channel skip the send.
    case "account_created":
      return [sanitizeTemplateParam(payload.firstName ?? "")];
    case "onboarding_in_week":
      return [sanitizeTemplateParam(payload.waLine ?? "")];
  }
};

// ─── Group keys (D-08) ─────────────────────────────────────────────────────────

// Keys are scoped per recipient by the notification document itself (recipient + groupKey), so
// the key only needs type and target. points_awarded groups per Cairo day.
export const buildGroupKey = ({
  type,
  targetId,
  cairoDay,
}: {
  type: NotificationType;
  targetId: string;
  cairoDay?: string;
}): string => {
  if (type === "points_awarded" && cairoDay) return `${type}:${cairoDay}`;
  return `${type}:${targetId}`;
};

// ─── Deep links (D-09) ─────────────────────────────────────────────────────────

const FALLBACK_LINK = "/notifications";

// Same-origin relative only: starts with one '/', no backslash, no control chars. Used when a
// link is built and again in the service worker click handler.
export const isSafeRelativePath = (path: string): boolean => {
  if (typeof path !== "string" || path.length === 0) return false;
  if (!path.startsWith("/") || path.startsWith("//")) return false;
  if (path.includes("\\")) return false;
  if (/[\u0000-\u001f\u007f]/.test(path)) return false;
  return true;
};

const storeReviewLink = (payload: NotificationPayload, withComment: boolean): string => {
  const store = payload.storeSlug || payload.storeId;
  if (!store) return FALLBACK_LINK;
  const base = `/store/${encodeURIComponent(store)}`;
  if (!payload.reviewId) return base;
  let link = `${base}?focusReview=${encodeURIComponent(payload.reviewId)}`;
  if (withComment && payload.commentId) link += `&focusComment=${encodeURIComponent(payload.commentId)}`;
  return link;
};

export const buildDeepLink = ({
  type,
  payload,
}: {
  type: NotificationType;
  payload: NotificationPayload;
}): string => {
  let link: string;
  switch (type) {
    case "claim_approved":
    case "claim_rejected":
      link = "/merchant";
      break;
    case "store_review_received":
    case "review_merchant_replied":
    case "review_liked":
      link = storeReviewLink(payload, false);
      break;
    case "review_commented":
      link = storeReviewLink(payload, true);
      break;
    case "tier_reached":
    case "points_awarded":
      link = payload.userId ? `/u/${encodeURIComponent(payload.userId)}#rewards` : FALLBACK_LINK;
      break;
    case "escalation_opened":
      link = payload.escalationId
        ? `/admin/moderator-tasks?tab=escalations&focus=${encodeURIComponent(payload.escalationId)}`
        : "/admin/moderator-tasks?tab=escalations";
      break;
    case "welcome":
      link = payload.userId ? `/u/${encodeURIComponent(payload.userId)}` : FALLBACK_LINK;
      break;
    case "onboarding_day3":
    case "onboarding_day7":
      if (payload.ladderRung === 1 || payload.ladderRung === 2) {
        link = storeReviewLink(payload, false);
      } else if ((payload.ladderRung === 3 || payload.ladderRung === 4) && payload.storeSlug) {
        link = `/store/${encodeURIComponent(payload.storeSlug)}?writeReview=1`;
      } else {
        link = "/stores";
      }
      break;
  }
  return isSafeRelativePath(link) ? link : FALLBACK_LINK;
};

// Tags a push/email click so the landing page can capture notification_clicked. The params go in
// the query string before any '#'.
export const withNotificationSource = ({
  deepLink,
  source,
  type,
}: {
  deepLink: string;
  source: "push" | "email" | "whatsapp";
  type: NotificationType;
}): string => {
  const hashIndex = deepLink.indexOf("#");
  const beforeHash = hashIndex === -1 ? deepLink : deepLink.slice(0, hashIndex);
  const hash = hashIndex === -1 ? "" : deepLink.slice(hashIndex);
  const sep = beforeHash.includes("?") ? "&" : "?";
  return `${beforeHash}${sep}nsrc=${source}&ntype=${type}${hash}`;
};

// ─── Preference matrix rows ────────────────────────────────────────────────────

// Ordered rows of the /profile matrix. One row may write several types (claim decision covers
// approve and reject). Admin types are not in the matrix.
export type PreferenceRowKey =
  | "account"
  | "claim_decision"
  | "store_review"
  | "merchant_reply"
  | "comment"
  | "like"
  | "tier"
  | "points";

export type PreferenceRow = {
  key: PreferenceRowKey;
  types: NotificationType[];
  group: NotificationGroup;
  requires?: "anyClaim" | "approvedClaim";
};

export const PREFERENCE_ROWS: PreferenceRow[] = [
  // Phase 49 (D-18): one row for every onboarding message, rendered first.
  { key: "account", types: ["welcome", "onboarding_day3", "onboarding_day7"], group: "account" },
  { key: "claim_decision", types: ["claim_approved", "claim_rejected"], group: "merchant", requires: "anyClaim" },
  { key: "store_review", types: ["store_review_received"], group: "merchant", requires: "approvedClaim" },
  { key: "merchant_reply", types: ["review_merchant_replied"], group: "reviews" },
  { key: "comment", types: ["review_commented"], group: "reviews" },
  { key: "like", types: ["review_liked"], group: "reviews" },
  { key: "tier", types: ["tier_reached"], group: "rewards" },
  { key: "points", types: ["points_awarded"], group: "rewards" },
];

// A row loses its WhatsApp cell only when it is digestable or when NO type in it can use WhatsApp.
// The account row mixes eligible (welcome, day 3) and ineligible (day 7, D-24) types and keeps a
// working toggle (D-18); writing the override for day 7 is harmless because shouldSend refuses
// WhatsApp for ineligible types. Every other row is homogeneous, so their behaviour is unchanged.
export const isWhatsappRowBlocked = (row: PreferenceRow): boolean =>
  row.types.some((t) => NOTIFICATION_REGISTRY[t].digestable) ||
  row.types.every((t) => !NOTIFICATION_REGISTRY[t].whatsappEligible);
